import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Send, Inbox, Clock, CheckCircle, XCircle, Share2, ArrowRight, RefreshCw, Copy, ExternalLink, User, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper functions - moved outside component
const getStatusConfig = (status) => {
  const configs = {
    pending: { 
      label: 'Bekliyor', 
      color: '#fbbf24', 
      bg: 'rgba(251, 191, 36, 0.15)',
      IconComponent: Clock
    },
    redeemed: { 
      label: 'Kullanıldı', 
      color: '#22c55e', 
      bg: 'rgba(34, 197, 94, 0.15)',
      IconComponent: CheckCircle
    },
    expired: { 
      label: 'Süresi Doldu', 
      color: '#ef4444', 
      bg: 'rgba(239, 68, 68, 0.15)',
      IconComponent: XCircle
    }
  };
  return configs[status] || configs.pending;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
};

const isExpiringSoon = (expiresAt) => {
  if (!expiresAt) return false;
  const expDate = new Date(expiresAt);
  const now = new Date();
  const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
  return daysLeft <= 3 && daysLeft > 0;
};

const getDaysLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const expDate = new Date(expiresAt);
  const now = new Date();
  const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
  return daysLeft > 0 ? daysLeft : 0;
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const IconComponent = config.IconComponent;
  return (
    <span 
      data-testid={`gift-status-${status}`}
      style={{
        background: config.bg,
        color: config.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}
    >
      <IconComponent size={12} />
      {config.label}
    </span>
  );
};

// Sent Gift Card - Extracted Component
const SentGiftCard = ({ gift, copiedCode, onShare, onCopy }) => {
  const statusConfig = getStatusConfig(gift.status);
  const daysLeft = getDaysLeft(gift.expires_at);
  const expiring = isExpiringSoon(gift.expires_at);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`sent-gift-card-${gift.display_code}`}
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        padding: '16px'
      }}
    >
      {/* Header Row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.2))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            {gift.gift_icon || '🎁'}
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>
              {gift.gift_name || 'Hediye'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
              {gift.credits_used || gift.credits_deducted || 0} kredi kullanıldı
            </div>
          </div>
        </div>
        <StatusBadge status={gift.status} />
      </div>

      {/* Details Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '10px',
        marginBottom: '12px',
        padding: '12px',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '10px'
      }}>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '2px' }}>
            Gönderim Tarihi
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
            {formatShortDate(gift.created_at)}
          </div>
        </div>
        
        <div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '2px' }}>
            Son Kullanım
          </div>
          <div style={{ 
            color: gift.status === 'pending' && expiring ? '#fbbf24' : 'rgba(255,255,255,0.8)', 
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {formatShortDate(gift.expires_at)}
            {gift.status === 'pending' && daysLeft !== null && daysLeft <= 7 && (
              <span style={{ 
                fontSize: '10px', 
                padding: '2px 6px', 
                borderRadius: '4px',
                background: expiring ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255,255,255,0.1)',
                color: expiring ? '#fbbf24' : 'rgba(255,255,255,0.6)'
              }}>
                {daysLeft} gün
              </span>
            )}
          </div>
        </div>

        <div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '2px' }}>
            Hediye Türü
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
            {gift.gift_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}
          </div>
        </div>

        <div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '2px' }}>
            Durum
          </div>
          <div style={{ color: statusConfig.color, fontSize: '13px' }}>
            {gift.status === 'redeemed' ? '✓ Alıcı kullandı' : 
             gift.status === 'expired' ? '✗ Süre doldu' : 
             '⏳ Bekliyor'}
          </div>
        </div>
      </div>

      {/* Message if exists */}
      {gift.message && (
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          fontStyle: 'italic',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '13px',
          borderLeft: '3px solid rgba(139, 92, 246, 0.5)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {gift.message}
        </div>
      )}

      {/* Actions */}
      {gift.status === 'pending' && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => onShare(gift)}
            data-testid={`share-gift-${gift.display_code}`}
            style={{
              flex: '1 1 auto',
              minWidth: '120px',
              padding: '10px',
              background: 'rgba(139, 92, 246, 0.15)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '10px',
              color: '#c084fc',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <Share2 size={14} />
            {copiedCode === gift.display_code ? 'Kopyalandı!' : 'Tekrar Paylaş'}
          </button>
          
          <button
            onClick={() => onCopy(gift.display_code, gift.display_code + '-code')}
            data-testid={`copy-code-${gift.display_code}`}
            style={{
              padding: '10px 14px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <Copy size={14} />
            {copiedCode === gift.display_code + '-code' ? '✓' : 'Kod'}
          </button>
        </div>
      )}

      {/* Display Code Footer */}
      <div style={{ 
        marginTop: '12px', 
        paddingTop: '12px', 
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'monospace' }}>
          Kod: {gift.display_code}
        </span>
        {gift.was_redeemed && gift.redeemed_at && (
          <span style={{ color: 'rgba(34, 197, 94, 0.7)', fontSize: '11px' }}>
            Kullanıldı: {formatShortDate(gift.redeemed_at)}
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Received Gift Card - Extracted Component
const ReceivedGiftCard = ({ gift, onNavigate }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`received-gift-card-${gift.display_code}`}
      style={{
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(16, 185, 129, 0.03))',
        border: '1px solid rgba(34, 197, 94, 0.15)',
        borderRadius: '16px',
        padding: '16px'
      }}
    >
      {/* Header Row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.2))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            {gift.gift_icon || '🎁'}
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>
              {gift.gift_name || 'Hediye'}
            </div>
            <div style={{ color: '#22c55e', fontSize: '12px', marginTop: '2px', fontWeight: '500' }}>
              +{gift.credits_received || 0} kredi kazanıldı
            </div>
          </div>
        </div>
        
        {/* Tamamlandı Badge */}
        <span style={{
          background: 'rgba(34, 197, 94, 0.15)',
          color: '#22c55e',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <CheckCircle size={12} />
          Tamamlandı
        </span>
      </div>

      {/* Sender Info */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '8px',
        marginBottom: '12px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'rgba(139, 92, 246, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <User size={16} color="#c084fc" />
        </div>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Gönderen</div>
          <div style={{ color: '#c084fc', fontSize: '14px', fontWeight: '500' }}>
            {gift.sender_name || 'Anonim'}
          </div>
        </div>
      </div>

      {/* Message if exists */}
      {gift.message && (
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '12px',
          borderLeft: '3px solid rgba(139, 92, 246, 0.5)'
        }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '4px' }}>
            Mesaj
          </div>
          <div style={{ 
            fontStyle: 'italic',
            color: 'rgba(255,255,255,0.8)',
            fontSize: '14px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {gift.message}
          </div>
        </div>
      )}

      {/* Start Reading Button */}
      {gift.reading_path && (
        <button
          onClick={() => onNavigate(gift.reading_path)}
          data-testid={`start-reading-${gift.display_code}`}
          style={{
            width: '100%',
            padding: '12px',
            background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          Falını Başlat
          <ArrowRight size={16} />
        </button>
      )}

      {/* Footer with date */}
      <div style={{ 
        marginTop: '12px', 
        paddingTop: '12px', 
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'monospace' }}>
          Kod: {gift.display_code}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
          Alındı: {formatDate(gift.redeemed_at)}
        </span>
      </div>
    </motion.div>
  );
};

// Main Component
const GiftHistoryTab = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('sent');
  const [sentGifts, setSentGifts] = useState([]);
  const [receivedGifts, setReceivedGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchGiftHistory();
  }, []);

  const fetchGiftHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Giriş yapmanız gerekiyor');
        setLoading(false);
        return;
      }

      const sentRes = await fetch(`${API_URL}/api/gifts/my-sent?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sentData = await sentRes.json();
      if (sentRes.ok) setSentGifts(sentData.gifts || []);

      const receivedRes = await fetch(`${API_URL}/api/gifts/my-received?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const receivedData = await receivedRes.json();
      if (receivedRes.ok) setReceivedGifts(receivedData.gifts || []);

    } catch (err) {
      setError('Veriler yüklenirken hata oluştu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, code) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleShare = async (gift) => {
    const baseUrl = window.location.origin;
    const shareText = `🎁 Sana bir hediye var!\n\n${gift.gift_name} hediyesi seni bekliyor!\n\nKod: ${gift.display_code}\n\nMysticAI'da hediyeni al:\n${baseUrl}/gift/redeem`;
    
    if (navigator.share) {
      try {
        await navigator.share({ 
          title: 'MysticAI Hediye',
          text: shareText 
        });
      } catch (err) {
        copyToClipboard(shareText, gift.display_code);
      }
    } else {
      copyToClipboard(shareText, gift.display_code);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          style={{ display: 'inline-block' }}
        >
          <RefreshCw size={32} color="#8b5cf6" />
        </motion.div>
        <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '16px' }}>Hediyeler yükleniyor...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>😔</div>
        <p style={{ color: '#ef4444', marginBottom: '8px' }}>{error}</p>
        <button
          onClick={fetchGiftHistory}
          data-testid="retry-load-gifts"
          style={{
            marginTop: '16px',
            padding: '12px 24px',
            background: 'rgba(139, 92, 246, 0.2)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '10px',
            color: '#c084fc',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ padding: '0 4px', maxWidth: '800px', margin: '0 auto' }}
      data-testid="gift-history-tab"
    >
      {/* Back Button / Breadcrumb */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/')}
          data-testid="back-to-dashboard"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
            padding: '10px 16px',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
            e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
          }}
        >
          <ArrowLeft size={16} />
          Dashboard
        </button>
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h2 style={{ 
          color: 'white', 
          fontSize: '24px', 
          fontWeight: '700', 
          margin: '0 0 8px',
          background: 'linear-gradient(135deg, #c084fc, #f472b6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Hediyelerim
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>
          Gönderdiğiniz ve aldığınız hediyeler
        </p>
      </div>

      {/* Section Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        background: 'rgba(255,255,255,0.03)',
        padding: '6px',
        borderRadius: '14px'
      }}>
        <button
          onClick={() => setActiveSection('sent')}
          data-testid="sent-gifts-tab"
          style={{
            flex: 1,
            padding: '14px 12px',
            background: activeSection === 'sent' 
              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(236, 72, 153, 0.15))' 
              : 'transparent',
            border: activeSection === 'sent' 
              ? '1px solid rgba(139, 92, 246, 0.3)' 
              : '1px solid transparent',
            borderRadius: '10px',
            color: activeSection === 'sent' ? '#c084fc' : 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Send size={16} />
          Gönderdiğim
          <span style={{
            background: activeSection === 'sent' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255,255,255,0.1)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '12px'
          }}>
            {sentGifts.length}
          </span>
        </button>
        
        <button
          onClick={() => setActiveSection('received')}
          data-testid="received-gifts-tab"
          style={{
            flex: 1,
            padding: '14px 12px',
            background: activeSection === 'received' 
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.25), rgba(16, 185, 129, 0.15))' 
              : 'transparent',
            border: activeSection === 'received' 
              ? '1px solid rgba(34, 197, 94, 0.3)' 
              : '1px solid transparent',
            borderRadius: '10px',
            color: activeSection === 'received' ? '#22c55e' : 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Inbox size={16} />
          Aldığım
          <span style={{
            background: activeSection === 'received' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255,255,255,0.1)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '12px'
          }}>
            {receivedGifts.length}
          </span>
        </button>
      </div>

      {/* Gift Lists - Responsive Grid */}
      {activeSection === 'sent' ? (
        sentGifts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(139, 92, 246, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '36px'
            }}>
              🎁
            </div>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', marginBottom: '8px' }}>
              Henüz hediye göndermediniz
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '20px' }}>
              Hediye Et butonuna tıklayarak arkadaşlarınıza fal hediye edebilirsiniz
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '16px' 
          }}>
            {sentGifts.map((gift, index) => (
              <SentGiftCard 
                key={gift.display_code + index} 
                gift={gift} 
                copiedCode={copiedCode}
                onShare={handleShare}
                onCopy={copyToClipboard}
              />
            ))}
          </div>
        )
      ) : (
        receivedGifts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(34, 197, 94, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '36px'
            }}>
              📬
            </div>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', marginBottom: '8px' }}>
              Henüz hediye almadınız
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
              Arkadaşlarınızdan hediye kodu aldığınızda Hediyemi Kullan sayfasından kullanabilirsiniz
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '16px' 
          }}>
            {receivedGifts.map((gift, index) => (
              <ReceivedGiftCard 
                key={gift.display_code + index} 
                gift={gift} 
                onNavigate={navigate}
              />
            ))}
          </div>
        )
      )}

      {/* Refresh Button */}
      <div style={{ textAlign: 'center', marginTop: '24px', paddingBottom: '20px' }}>
        <button
          onClick={fetchGiftHistory}
          data-testid="refresh-gifts"
          style={{
            padding: '10px 20px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            color: 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <RefreshCw size={14} />
          Yenile
        </button>
      </div>
    </motion.div>
  );
};

export default GiftHistoryTab;
